import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DumontLogo } from "../../../Generic/Icons";
import { useAppState } from "../../../../context/StateContext";
import { dumontColors } from "../../colors";

interface PromptCard {
  id: string;
  title: string;
  description: string;
}

const DISPATCH_PROMPT_CARDS = [
  {
    id: "today-orders",
    titleKey: "dumontDispatchShowTodayOrders",
    descriptionKey: "dumontDispatchShowTodayOrdersDesc",
  },
  {
    id: "find-customer",
    titleKey: "dumontDispatchFindCustomer",
    descriptionKey: "dumontDispatchFindCustomerDesc",
  },
  {
    id: "track-status",
    titleKey: "dumontDispatchTrackStatus",
    descriptionKey: "dumontDispatchTrackStatusDesc",
  },
  {
    id: "optimize-routes",
    titleKey: "dumontDispatchOptimizeRoutes",
    descriptionKey: "dumontDispatchOptimizeRoutesDesc",
  },
];

interface FullscreenDispatchPromptsProps {
  onPromptClick?: (prompt: string) => void;
}

export default function FullscreenDispatchPrompts({
  onPromptClick,
}: FullscreenDispatchPromptsProps) {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const userName = currentUser?.name || "User";
  const [selectedCard, setSelectedCard] = useState<string | null>(null);

  const handleCardClick = (card: typeof DISPATCH_PROMPT_CARDS[0]) => {
    setSelectedCard(card.id);
    onPromptClick?.(translate(card.titleKey));
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        width: "100%",
        maxWidth: "1200px",
        margin: "0 auto",
        height: "100%",
      }}
    >
      {/* Logo e Título */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "4px",
          marginBottom: "48px",
        }}
      >
        <Box
          sx={{
            width: "40px",
            height: "40px",
            borderRadius: "4px",
          }}
        >
          <DumontLogo size={40} />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            width: "100%",
            marginTop: "4px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontSize: "20px",
              fontWeight: 500,
              lineHeight: "1.4em",
              color: "#475467",
              textAlign: "center",
            }}
          >
            Dumont AI
          </Typography>
        </Box>
      </Box>

      {/* Prompts Section */}
      <Box sx={{ width: "100%", maxWidth: "1152px" }}>
        <Box
          sx={{
            padding: "0px 8px",
            height: "18.19px",
            display: "flex",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontSize: "13px",
              fontWeight: 500,
              lineHeight: "1.4em",
              color: dumontColors.textTertiary,
              textAlign: "left",
            }}
          >
            {translate("dumontDispatchQuickActions")}
          </Typography>
        </Box>

        {/* Grid de Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
            rowGap: "8px",
          }}
        >
          {DISPATCH_PROMPT_CARDS.map((card) => {
            const isSelected = selectedCard === card.id;
            return (
              <Box
                key={card.id}
                onClick={() => handleCardClick(card)}
                sx={{
                  height: "68.39px",
                  padding: "12px 0px 12px 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  border: isSelected ? "1px solid #00A49F" : "1px solid #E5E7EB",
                  borderRadius: "8px",
                  cursor: "pointer",
                  boxSizing: "border-box",
                  backgroundColor: isSelected ? "rgba(0, 164, 159, 0.1)" : "transparent",
                  boxShadow: isSelected ? "0px 2px 8px 0px rgba(2, 131, 128, 0.15)" : "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: isSelected ? "rgba(0, 164, 159, 0.1)" : "#F9FAFB",
                    borderColor: "#00A49F",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Manrope",
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "1.4em",
                      color: "#1D2939",
                      textAlign: "left",
                    }}
                  >
                    {translate(card.titleKey)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: "16.8px",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Manrope",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "1.4em",
                      color: dumontColors.textTertiary,
                      textAlign: "left",
                    }}
                  >
                    {translate(card.descriptionKey)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
